import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import OneSNavPanel from "@/components/feature/OneSNavPanel";
import { useDiscussions } from "@/hooks/useDiscussions";
import ReportsModal from "@/pages/rop/components/ReportsModal";
import TaskConfirmationModal from "@/pages/rop/components/TaskConfirmationModal";
import LostDealsModal from "@/pages/rop/components/LostDealsModal";
import DecliningClientsModal from "@/pages/rop/components/DecliningClientsModal";
import ForgottenClientsModal from "@/pages/rop/components/ForgottenClientsModal";
import RepeatSalesModal from "@/pages/rop/components/RepeatSalesModal";
import UnansweredOffersModal from "@/pages/rop/components/UnansweredOffersModal";
import DepartmentAnalysisModal from "@/pages/rop/components/DepartmentAnalysisModal";
import KpiSettingsTab from "@/pages/rop/components/KpiSettingsTab";
import DepartmentTab from "@/pages/rop/components/DepartmentTab";
import {
  tasks,
  managerKPIs,
  clientAnalysis,
  departmentStages,
  reportTemplates,
  stuckDeals,
  dormantClients,
  unpaidInvoices,
  aiInsights,
  managerHelpItems,
  aiTaskSuggestions,
  unansweredOffers,
  decliningClients,
  lostDeals,
  forgottenClients,
  repeatSalePotentials,
  departmentWeakSpots,
  managersLagging,
  planRevenue,
  factRevenue,
  planMargin,
  factMargin,
  forecastRevenue,
  forecastMargin,
  totalLostRevenue,
  totalRepeatPotential,
  totalOverdueDebt,
  AITaskSuggestion,
  departments,
} from "@/mocks/rop";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

function DashboardWidgetCard({ icon, label, count, amount, iconBg, color }: { icon: string; label: string; count: number; amount?: number; iconBg: string; color: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#D5CA8A] flex-1 min-w-0">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <i className={`${icon} text-[18px] ${color}`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#666666] leading-tight truncate">{label}</p>
        <p className="text-[18px] font-bold text-[#2D2D2D] leading-tight mt-0.5">{count}</p>
        {amount !== undefined && (
          <p className="text-[10px] text-[#666666] leading-tight">{formatMoney(amount)}</p>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, icon, children, action, onClick, accent }: { title: string; icon: string; children: React.ReactNode; action?: string; onClick?: () => void; accent?: string }) {
  return (
    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
        <div className="flex items-center gap-1.5">
          <i className={`${icon} text-[12px] text-[#2D2D2D]`}></i>
          <span className="text-[11px] font-semibold text-[#2D2D2D]">{title}</span>
          {accent && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#DC2626]">{accent}</span>
          )}
        </div>
        {action && (
          <button onClick={onClick} className="text-[10px] text-[#2D2D2D] hover:bg-[#E8DB6C] px-2 py-0.5 rounded transition-colors cursor-pointer whitespace-nowrap">
            {action}
          </button>
        )}
      </div>
      <div className="p-3">
        {children}
      </div>
    </div>
  );
}

export default function ROPPage() {
  const navigate = useNavigate();
  const { toggle: toggleDiscussions } = useDiscussions();
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showManagerKPIModal, setShowManagerKPIModal] = useState(false);
  const [showFunnelModal, setShowFunnelModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showTaskConfirmModal, setShowTaskConfirmModal] = useState(false);
  const [showLostDealsModal, setShowLostDealsModal] = useState(false);
  const [showDecliningModal, setShowDecliningModal] = useState(false);
  const [showForgottenModal, setShowForgottenModal] = useState(false);
  const [showRepeatSalesModal, setShowRepeatSalesModal] = useState(false);
  const [showUnansweredModal, setShowUnansweredModal] = useState(false);
  const [showDeptAnalysisModal, setShowDeptAnalysisModal] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'dashboard' | 'dep1' | 'dep2' | 'dep3' | 'kpiSettings'>('dashboard');

  // Агрегаты по всем отделам — суммируем всех менеджеров из всех отделов
  const dashboardTotals = useMemo(() => {
    const allManagers = departments.flatMap(d => d.managers);
    return {
      activeDeals: allManagers.reduce((s, m) => s + m.activeDeals, 0),
      activeDealsAmount: allManagers.reduce((s, m) => s + m.activeDealsAmount, 0),
      unansweredOffers: allManagers.reduce((s, m) => s + m.unansweredOffers, 0),
      unansweredOffersAmount: allManagers.reduce((s, m) => s + m.unansweredOffersAmount, 0),
      ordersInProgress: allManagers.reduce((s, m) => s + m.ordersInProgress, 0),
      ordersInProgressAmount: allManagers.reduce((s, m) => s + m.ordersInProgressAmount, 0),
      warehouseAssembly: allManagers.reduce((s, m) => s + m.warehouseAssembly, 0),
      deliveriesToday: allManagers.reduce((s, m) => s + m.deliveriesToday, 0),
      clientsWithDebt: allManagers.reduce((s, m) => s + m.clientsWithDebt, 0),
      debtAmount: allManagers.reduce((s, m) => s + m.debtAmount, 0),
      paymentsToday: allManagers.reduce((s, m) => s + m.paymentsToday, 0),
      paymentsAmount: allManagers.reduce((s, m) => s + m.paymentsAmount, 0),
    };
  }, []);
  const totalManagers = useMemo(() => departments.reduce((s, d) => s + d.managers.length, 0), []);

  return (
    <div className="h-screen flex flex-col bg-[#FFF9E6] overflow-hidden"
      style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
    >
      {/* 1C Title Bar */}
      <div className="h-[28px] flex items-center justify-between bg-[#FBED9E] px-3 flex-shrink-0 select-none border-b border-[#D5CA8A]">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <i className="ri-menu-line text-[#666666] text-[13px]"></i>
            <span className="text-[11px] text-[#2D2D2D] font-medium">Методическая база / Управление торговлей, редакция 11 (1С:Предприятие)</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-[#666666]">Елена Голубева</span>
          <div className="w-px h-3 bg-[#D5CA8A]" />
          <i className="ri-settings-3-line text-[#666666] text-[13px] cursor-pointer hover:text-[#2D2D2D]"></i>
          <i className="ri-question-line text-[#666666] text-[13px] cursor-pointer hover:text-[#2D2D2D]"></i>
          <i className="ri-close-line text-[#666666] text-[13px] cursor-pointer hover:text-[#2D2D2D]"></i>
        </div>
      </div>

      {/* 1C Command Bar */}
      <div className="h-[32px] flex items-center gap-1 px-2 bg-[#FFF9E6] border-b border-[#D5CA8A] flex-shrink-0 select-none">
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-file-add-line text-[11px]"></i>
            <span>Создать</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-file-copy-line text-[11px]"></i>
            <span>Скопировать</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-delete-bin-line text-[11px]"></i>
            <span>Удалить</span>
          </button>
        </div>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-filter-line text-[11px]"></i>
            <span>Фильтры</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-group-line text-[11px]"></i>
            <span>Группировки</span>
          </button>
        </div>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-file-list-3-line text-[11px]"></i>
            <span>Отчёт</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
            <i className="ri-printer-line text-[11px]"></i>
            <span>Печать</span>
          </button>
        </div>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-[11px]"></i>
            <span>Заказы клиентов</span>
          </button>
          <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
          <div className="flex items-center gap-1 bg-[#FDF2B5] rounded p-[2px]">
            <span className="px-2 py-[2px] bg-[#E8DB6C] text-[#2D2D2D] rounded text-[11px] font-medium flex items-center gap-1 whitespace-nowrap">
              <i className="ri-vip-crown-line text-[10px]"></i>
              РОП
            </span>
          </div>
          <button
            onClick={() => navigate("/client360")}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap"
          >
            <i className="ri-user-search-line text-[10px]"></i>
            <span>Клиент 360</span>
          </button>
          <button
            onClick={() => navigate("/manager-workspace")}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap"
          >
            <i className="ri-user-settings-line text-[10px]"></i>
            <span>Рабочее место</span>
          </button>
          <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
          <button
            onClick={toggleDiscussions}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap"
          >
            <i className="ri-chat-3-line text-[11px]"></i>
            <span>Обсуждения</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="h-[32px] flex items-center bg-[#FFF9E6] border-b border-[#D5CA8A] px-1 gap-0 select-none">
        <button className="flex items-center gap-1 px-2 py-1 text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
          <i className="ri-arrow-left-s-line text-[11px]"></i>
        </button>
        <button className="flex items-center gap-1 px-2 py-1 text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap">
          <i className="ri-arrow-right-s-line text-[11px]"></i>
        </button>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-1 px-3 py-1 text-[11px] rounded-t cursor-pointer transition-colors border border-b-0 bg-[#FFF9E6] text-[#666666] border-[#FFF9E6] hover:bg-[#FDF2B5] whitespace-nowrap"
        >
          <i className="ri-file-list-2-line text-[10px]"></i>
          Заказы клиентов
        </button>
        <button className="flex items-center gap-1 px-3 py-1 text-[11px] rounded-t cursor-pointer transition-colors border border-b-0 bg-white text-[#2D2D2D] border-[#D5CA8A] font-medium whitespace-nowrap">
          <i className="ri-vip-crown-line text-[10px]"></i>
          РОП
          <i className="ri-close-line text-[10px] hover:text-[#666666] ml-1"></i>
        </button>
        <div className="ml-auto flex items-center gap-1 pr-2 text-[10px]">
          <span className="text-[#666666]">Методическая база</span>
          <span className="text-[#666666]">/</span>
          <span className="text-[#666666]">Управление торговлей</span>
          <span className="text-[#666666]">/</span>
          <span className="text-[#2D2D2D] font-medium">РОП</span>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        <OneSNavPanel />

        <div className="flex-1 overflow-y-auto p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-[16px] font-semibold text-[#2D2D2D]">РОП — руководитель отдела продаж</h1>
              <p className="text-[11px] text-[#666666]">Сводный дашборд и аналитика по отделам</p>
            </div>
          </div>

          {/* Sub Tab Bar */}
          <div className="flex items-center gap-0 mb-4 border-b border-[#D5CA8A]">
            {([
              { key: 'dashboard' as const, label: 'Дашборд', icon: 'ri-dashboard-line' },
              { key: 'dep1' as const, label: '1 отдел', icon: 'ri-building-2-line' },
              { key: 'dep2' as const, label: '2 отдел', icon: 'ri-building-2-line' },
              { key: 'dep3' as const, label: '3 отдел', icon: 'ri-building-2-line' },
              { key: 'kpiSettings' as const, label: 'Настройки KPI', icon: 'ri-settings-3-line' },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSubTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-medium cursor-pointer transition-colors border-b-2 -mb-[1px] whitespace-nowrap ${
                  activeSubTab === tab.key
                    ? 'text-[#2D2D2D] border-[#D97706] bg-white rounded-t'
                    : 'text-[#666666] border-transparent hover:text-[#2D2D2D] hover:bg-[#FDF2B5]/50 rounded-t'
                }`}
              >
                <i className={`${tab.icon} text-[12px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeSubTab === 'dashboard' && (
          <>
            {/* KPI Cards - агрегаты по всем трём отделам */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-bar-chart-grouped-line text-[13px] text-[#666666]"></i>
                <span className="text-[11px] font-semibold text-[#666666]">Сводка по всем отделам</span>
                <span className="text-[10px] text-[#999999]">· {departments.length} отдела · {totalManagers} менеджеров</span>
              </div>
              <div className="flex flex-nowrap gap-2 overflow-x-auto">
                <DashboardWidgetCard
                  icon="ri-focus-3-line" label="Активные сделки" count={dashboardTotals.activeDeals} amount={dashboardTotals.activeDealsAmount}
                  iconBg="bg-[#D4EDDA]" color="text-[#2D6A4F]"
                />
                <DashboardWidgetCard
                  icon="ri-mail-unread-line" label="КП без ответа" count={dashboardTotals.unansweredOffers} amount={dashboardTotals.unansweredOffersAmount}
                  iconBg="bg-[#FEF3C7]" color="text-[#D97706]"
                />
                <DashboardWidgetCard
                  icon="ri-file-list-3-line" label="Заказы в работе" count={dashboardTotals.ordersInProgress} amount={dashboardTotals.ordersInProgressAmount}
                  iconBg="bg-[#E8F4FD]" color="text-[#0369A1]"
                />
                <DashboardWidgetCard
                  icon="ri-tools-line" label="Сборка на складе" count={dashboardTotals.warehouseAssembly}
                  iconBg="bg-[#FDF2B5]" color="text-[#B45309]"
                />
                <DashboardWidgetCard
                  icon="ri-truck-line" label="Доставки сегодня" count={dashboardTotals.deliveriesToday}
                  iconBg="bg-[#D4EDDA]" color="text-[#2D6A4F]"
                />
                <DashboardWidgetCard
                  icon="ri-money-dollar-circle-line" label="Клиенты с долгом" count={dashboardTotals.clientsWithDebt} amount={dashboardTotals.debtAmount}
                  iconBg="bg-[#FEE2E2]" color="text-[#DC2626]"
                />
                <DashboardWidgetCard
                  icon="ri-bank-card-line" label="Оплаты сегодня" count={dashboardTotals.paymentsToday} amount={dashboardTotals.paymentsAmount}
                  iconBg="bg-[#D4EDDA]" color="text-[#2D6A4F]"
                />
              </div>
            </div>

            {/* AI Insights - expanded */}
            <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden mb-4">
              <div className="flex items-center justify-between px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                <div className="flex items-center gap-1.5">
                  <i className="ri-lightbulb-line text-[14px] text-[#D97706]"></i>
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">AI-выводы (15 сценариев анализа)</span>
                </div>
                <span className="text-[9px] text-[#666666]">Обновлено: сегодня, 09:15</span>
              </div>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {aiInsights.map((insight) => (
                  <div key={insight.type} className="flex items-start gap-2 p-2 rounded-lg bg-[#FDF2B5]/50 hover:bg-[#FDF2B5] transition-colors cursor-pointer">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      insight.priority === "Высокий" ? "bg-[#FEE2E2]" : "bg-[#FEF3C7]"
                    }`}>
                      <i className={`${
                        insight.type === "stuck_deals" ? "ri-focus-3-line" :
                        insight.type === "dormant_clients" ? "ri-user-unfollow-line" :
                        insight.type === "unpaid_invoices" ? "ri-file-list-3-line" :
                        insight.type === "declining" ? "ri-arrow-down-circle-line" :
                        insight.type === "lost_deals" ? "ri-close-circle-line" :
                        "ri-task-line"
                      } text-[16px] ${
                        insight.priority === "Высокий" ? "text-[#DC2626]" : "text-[#D97706]"
                      }`}></i>
                    </div>
                    <div>
                      <p className="text-[12px] font-semibold text-[#2D2D2D]">
                        {insight.count} {insight.description}
                      </p>
                      <p className="text-[10px] text-[#666666]">{insight.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Task Confirmation + Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              {/* AI Task Confirmation */}
              <div className="lg:col-span-2 bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-robot-line text-[14px] text-[#D97706]"></i>
                    <span className="text-[11px] font-semibold text-[#2D2D2D]">AI-задачи на подтверждение ({aiTaskSuggestions.length})</span>
                  </div>
                  <button
                    onClick={() => setShowTaskConfirmModal(true)}
                    className="text-[10px] text-[#2D2D2D] hover:bg-[#E8DB6C] px-2 py-0.5 rounded transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Открыть все
                  </button>
                </div>
                <div className="p-3 space-y-1.5">
                  {aiTaskSuggestions.slice(0, 5).map((task: AITaskSuggestion) => (
                    <div key={task.id} className="flex items-center gap-2 p-2 rounded hover:bg-[#FDF2B5]/20 transition-colors border-b border-[#D5CA8A]/20 last:border-0">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        task.priority === "Критично" ? "bg-[#DC2626]" :
                        task.priority === "Высокий" ? "bg-[#D97706]" :
                        task.priority === "Средний" ? "bg-[#0369A1]" :
                        "bg-[#2D6A4F]"
                      }`}></span>
                      <span className="text-[10px] text-[#2D2D2D] truncate flex-1">{task.title}</span>
                      <span className="text-[9px] text-[#666666] whitespace-nowrap">{task.assignee}</span>
                      <button className="flex items-center gap-0.5 px-2 py-1 bg-[#E8DB6C] hover:bg-[#D4C55A] text-[#2D2D2D] rounded text-[9px] font-medium cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-check-line text-[10px]"></i>
                        Создать
                      </button>
                    </div>
                  ))}
                  {aiTaskSuggestions.length > 5 && (
                    <button
                      onClick={() => setShowTaskConfirmModal(true)}
                      className="w-full text-center text-[10px] text-[#666666] hover:text-[#2D2D2D] py-1 cursor-pointer"
                    >
                      + ещё {aiTaskSuggestions.length - 5} задач...
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                  <i className="ri-flashlight-line text-[14px] text-[#D97706]"></i>
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Быстрые действия</span>
                </div>
                <div className="p-3 space-y-2">
                  <button onClick={() => setShowTaskConfirmModal(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FDF2B5] hover:bg-[#E8DB6C] text-[#2D2D2D] text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-robot-line text-[13px]"></i>
                    AI-задачи ({aiTaskSuggestions.length})
                  </button>
                  <button onClick={() => setShowReportModal(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FDF2B5] hover:bg-[#E8DB6C] text-[#2D2D2D] text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-file-list-3-line text-[13px]"></i>
                    Отчеты ({reportTemplates.length})
                  </button>
                  <button onClick={() => setShowDeptAnalysisModal(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FDF2B5] hover:bg-[#E8DB6C] text-[#2D2D2D] text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-pie-chart-line text-[13px]"></i>
                    Анализ отдела
                  </button>
                  <button onClick={() => setShowManagerKPIModal(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FDF2B5] hover:bg-[#E8DB6C] text-[#2D2D2D] text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bar-chart-line text-[13px]"></i>
                    KPI менеджеров
                  </button>
                </div>
              </div>
            </div>

            {/* Section Grid - Row 1: Долги, Зависшие сделки, КП без ответа */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              {/* Долги и дебиторская задолженность */}
              <SectionCard title="Просроченная дебиторская задолженность" icon="ri-money-dollar-circle-line" action="Детали" onClick={() => setShowClientModal(true)} accent={`${formatMoney(totalOverdueDebt)}`}>
                <div className="space-y-1.5">
                  {unpaidInvoices.filter(i => i.daysOverdue > 0).sort((a, b) => b.daysOverdue - a.daysOverdue).slice(0, 5).map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[140px]">{inv.client.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{formatMoney(inv.amount)}</span>
                      <span className={`font-medium ${inv.daysOverdue > 100 ? "text-[#DC2626]" : "text-[#D97706]"}`}>{inv.daysOverdue} дн.</span>
                    </div>
                  ))}
                  <div className="mt-2 pt-2 border-t border-[#D5CA8A]/50">
                    <p className="text-[10px] text-[#666666]">Просрочено: {formatMoney(totalOverdueDebt)} | Счетов: {unpaidInvoices.filter(i => i.daysOverdue > 0).length}</p>
                  </div>
                </div>
              </SectionCard>

              {/* Зависшие сделки */}
              <SectionCard title="Сделки без следующего действия" icon="ri-focus-3-line" action="Все ({stuckDeals.length})" onClick={() => setShowFunnelModal(true)}>
                <div className="space-y-1.5">
                  {stuckDeals.sort((a, b) => b.daysWithoutAction - a.daysWithoutAction).slice(0, 5).map((deal) => (
                    <div key={deal.id} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[120px]">{deal.client.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{formatMoney(deal.amount)}</span>
                      <span className={`font-medium ${deal.daysWithoutAction > 30 ? "text-[#DC2626]" : "text-[#D97706]"}`}>{deal.daysWithoutAction} дн.</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* КП без ответа */}
              <SectionCard title="КП без ответа" icon="ri-mail-unread-line" action="Детали" onClick={() => setShowUnansweredModal(true)} accent={`${unansweredOffers.length}`}>
                <div className="space-y-1.5">
                  {unansweredOffers.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[120px]">{o.client.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{formatMoney(o.amount)}</span>
                      <span className={o.daysWithoutAnswer > 14 ? "text-[#DC2626] font-medium" : "text-[#D97706]"}>{o.daysWithoutAnswer} дн.</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            {/* Section Grid - Row 2: Давно не покупали, Снижение закупок, Потерянные сделки */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              {/* Клиенты давно не покупали */}
              <SectionCard title="Клиенты, которые давно не покупали" icon="ri-user-unfollow-line" action="Все ({dormantClients.length})" onClick={() => setShowClientModal(true)}>
                <div className="space-y-1.5">
                  {dormantClients.sort((a, b) => b.daysSinceOrder - a.daysSinceOrder).slice(0, 5).map((c) => (
                    <div key={c.name} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[130px]">{c.name.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{formatMoney(c.totalRevenue)}</span>
                      <span className={c.daysSinceOrder > 100 ? "text-[#DC2626] font-medium" : "text-[#D97706]"}>{c.daysSinceOrder} дн.</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Снижение закупок */}
              <SectionCard title="Клиенты со снижением закупок" icon="ri-arrow-down-circle-line" action="Детали" onClick={() => setShowDecliningModal(true)} accent={`${decliningClients.length}`}>
                <div className="space-y-1.5">
                  {decliningClients.sort((a, b) => b.declinePercent - a.declinePercent).map((c) => (
                    <div key={c.name} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[120px]">{c.name.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{formatMoney(c.prevRevenue)} → {formatMoney(c.currentRevenue)}</span>
                      <span className="text-[#DC2626] font-medium">-{c.declinePercent}%</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Потерянные сделки */}
              <SectionCard title="Потерянные сделки и причины отказов" icon="ri-close-circle-line" action="Детали" onClick={() => setShowLostDealsModal(true)} accent={`${formatMoney(totalLostRevenue)}`}>
                <div className="space-y-1.5">
                  {lostDeals.slice(0, 5).map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[110px]">{d.client.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#DC2626]">{formatMoney(d.amount)}</span>
                      <span className="text-[#666666] truncate max-w-[80px]">{d.reason.split(" (")[0]}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            {/* Section Grid - Row 3: Забытые клиенты, Повторные продажи, Воронка */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              {/* Забытые клиенты */}
              <SectionCard title="Клиенты без контакта / забытые" icon="ri-user-unfollow-line" action="Детали" onClick={() => setShowForgottenModal(true)}>
                <div className="space-y-1.5">
                  {forgottenClients.map((c) => (
                    <div key={c.name} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[130px]">{c.name.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{formatMoney(c.totalRevenue)}</span>
                      <span className="text-[#DC2626] font-medium">{c.daysWithoutContact} дн.</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Повторные продажи */}
              <SectionCard title="Повторные продажи и потенциал возврата" icon="ri-refresh-line" action="Детали" onClick={() => setShowRepeatSalesModal(true)} accent={`${formatMoney(totalRepeatPotential)}`}>
                <div className="space-y-1.5">
                  {repeatSalePotentials.map((p) => (
                    <div key={p.client} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] truncate max-w-[110px]">{p.client.replace("ООО ", "").replace("АО ", "")}</span>
                      <span className="text-[#666666]">{p.category}</span>
                      <span className="text-[#2D6A4F] font-medium">{formatMoney(p.potentialRevenue)}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Воронка продаж */}
              <SectionCard title="Воронка продаж" icon="ri-filter-3-line" action="Развернуть" onClick={() => setShowFunnelModal(true)}>
                <div className="space-y-1">
                  {departmentStages.slice(0, 4).map((stage, i) => {
                    const maxCount = departmentStages[0].count;
                    const width = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
                    return (
                      <div key={stage.name} className="flex items-center gap-2">
                        <div className="w-[100px] text-[9px] text-[#2D2D2D] text-right truncate">{stage.name}</div>
                        <div className="flex-1 h-4 bg-[#FDF2B5] rounded flex items-center" style={{ width: `${width}%` }}>
                          <span className="text-[9px] font-medium text-[#2D2D2D] px-1">{stage.count}</span>
                        </div>
                        <span className="text-[9px] text-[#666666] w-[28px]">{(stage.conversion * 100).toFixed(0)}%</span>
                      </div>
                    );
                  })}
                  <div className="mt-1 pt-1 border-t border-[#D5CA8A]/50 text-[10px] text-[#666666]">
                    Конверсия: вход 28 → закрыто 5 (17.9%)
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Finance + Analysis Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              {/* План/факт */}
              <SectionCard title="План / факт продаж" icon="ri-bar-chart-line">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Выручка (план)</span>
                    <span className="text-[#2D2D2D] font-medium">{formatMoney(planRevenue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Выручка (факт)</span>
                    <span className="text-[#2D6A4F] font-medium">{formatMoney(factRevenue)}</span>
                  </div>
                  <div className="w-full h-2 bg-[#D5CA8A]/30 rounded-full">
                    <div className="h-full bg-[#E8DB6C] rounded-full" style={{ width: `${Math.min((factRevenue / planRevenue) * 100, 100)}%` }}></div>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Выполнение</span>
                    <span className="text-[#2D6A4F] font-medium">{((factRevenue / planRevenue) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] mt-1">
                    <span className="text-[#666666]">Маржа (факт)</span>
                    <span className="text-[#2D6A4F] font-medium">{formatMoney(factMargin)}</span>
                  </div>
                </div>
              </SectionCard>

              {/* Прогноз продаж */}
              <SectionCard title="Прогноз продаж до конца месяца" icon="ri-line-chart-line">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Прогноз выручки</span>
                    <span className="text-[#2D6A4F] font-medium">{formatMoney(forecastRevenue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Прогноз маржи</span>
                    <span className="text-[#2D6A4F] font-medium">{formatMoney(forecastMargin)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Осталось дней</span>
                    <span className="text-[#2D2D2D] font-medium">13</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#666666]">Сделок в воронке</span>
                    <span className="text-[#2D2D2D] font-medium">{departmentStages[0].count}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-[#D5CA8A]/50">
                    <p className="text-[10px] text-[#666666]">
                      Вероятность выполнения плана: <span className="text-[#D97706] font-medium">91.4%</span>
                    </p>
                  </div>
                </div>
              </SectionCard>

              {/* KPI Менеджеров */}
              <SectionCard title="KPI менеджеров" icon="ri-team-line" action="Детали" onClick={() => setShowManagerKPIModal(true)}>
                <div className="space-y-1.5">
                  {managerKPIs.sort((a, b) => b.revenue - a.revenue).map((m) => (
                    <div key={m.name} className="flex items-center justify-between text-[10px]">
                      <span className="text-[#2D2D2D] font-medium">{m.name}</span>
                      <span className="text-[#666666]">{formatMoney(m.revenue)}</span>
                      <span className="text-[#2D6A4F]">{(m.conversion * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                  <div className="mt-2 pt-2 border-t border-[#D5CA8A]/50">
                    <p className="text-[10px] text-[#666666]">
                      Средняя конверсия: {(managerKPIs.reduce((s, m) => s + m.conversion, 0) / managerKPIs.length * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Analysis + Reports + Help Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Анализ отдела продаж */}
              <SectionCard title="Анализ всего отдела продаж" icon="ri-pie-chart-line" action="Развернуть" onClick={() => setShowDeptAnalysisModal(true)}>
                <div className="space-y-2">
                  <p className="text-[10px] text-[#666666]">Где теряются деньги и кто отстает:</p>
                  {departmentWeakSpots.map((spot) => (
                    <div key={spot.stage} className="flex items-start gap-1.5 text-[10px]">
                      <i className="ri-error-warning-line text-[#DC2626] text-[10px] mt-0.5 flex-shrink-0"></i>
                      <div>
                        <span className="text-[#DC2626] font-medium">{spot.stage}</span>
                        <span className="text-[#666666]"> — {spot.issue.split(".")[0]}.</span>
                      </div>
                    </div>
                  ))}
                  <div className="mt-2 pt-2 border-t border-[#D5CA8A]/50">
                    <span className="text-[10px] text-[#666666]">Отстающие: </span>
                    {managersLagging.filter((m, i, arr) => arr.findIndex(x => x.name === m.name) === i).map((m) => (
                      <span key={m.name} className="text-[10px] text-[#D97706]">{m.name} </span>
                    ))}
                  </div>
                </div>
              </SectionCard>

              {/* Отчеты + Помощь */}
              <div className="space-y-4">
                <SectionCard title="Отчеты для руководителей" icon="ri-file-list-3-line" action={`Все (${reportTemplates.length})`} onClick={() => setShowReportModal(true)}>
                  <div className="space-y-1">
                    {reportTemplates.slice(0, 8).map((r) => (
                      <div key={r.name} className="flex items-center justify-between text-[10px]">
                        <span className="text-[#2D2D2D] truncate max-w-[200px]">{r.name}</span>
                        <span className="text-[#666666] flex-shrink-0">{r.lastGenerated}</span>
                      </div>
                    ))}
                    {reportTemplates.length > 8 && (
                      <button
                        onClick={() => setShowReportModal(true)}
                        className="text-[10px] text-[#666666] hover:text-[#2D2D2D] cursor-pointer mt-1"
                      >
                        + ещё {reportTemplates.length - 8} отчётов...
                      </button>
                    )}
                  </div>
                </SectionCard>

                <SectionCard title="Помощь менеджерам" icon="ri-customer-service-line" action="Все подсказки" onClick={() => setShowHelpModal(true)}>
                  <div className="space-y-1">
                    {managerHelpItems.map((item, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-[10px] hover:bg-[#FDF2B5]/30 rounded px-1 py-0.5 cursor-pointer transition-colors">
                        <i className="ri-arrow-right-s-line text-[10px] text-[#666666] mt-0.5 flex-shrink-0"></i>
                        <span className="text-[#2D2D2D]">{item}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </div>

            {/* Bottom status bar */}
            <div className="h-[24px] flex items-center px-2 mt-4 bg-[#FBED9E] border border-[#D5CA8A] text-[10px] text-[#666666] select-none rounded">
              <span>Сценариев анализа: 15 | AI-инсайтов: {aiInsights.length} | Задач: {aiTaskSuggestions.length} на подтверждении | Отчётов: {reportTemplates.length}</span>
              <span className="ml-auto">Последнее обновление: {formatDate("2026-06-23")} | 1С:Предприятие 8.3.25.1448</span>
            </div>
          </>
        )}

        {activeSubTab === 'dep1' && (
          <DepartmentTab departmentIndex={0} />
        )}

        {activeSubTab === 'dep2' && (
          <DepartmentTab departmentIndex={1} />
        )}

        {activeSubTab === 'dep3' && (
          <DepartmentTab departmentIndex={2} />
        )}

        {activeSubTab === 'kpiSettings' && (
          <KpiSettingsTab />
        )}
        </div>
      </div>

      {/* ====== MODALS ====== */}

      {/* Task Confirmation Modal */}
      {showTaskConfirmModal && (
        <TaskConfirmationModal onClose={() => setShowTaskConfirmModal(false)} />
      )}

      {/* Reports Modal */}
      {showReportModal && (
        <ReportsModal onClose={() => setShowReportModal(false)} />
      )}

      {/* Lost Deals Modal */}
      {showLostDealsModal && (
        <LostDealsModal onClose={() => setShowLostDealsModal(false)} />
      )}

      {/* Declining Clients Modal */}
      {showDecliningModal && (
        <DecliningClientsModal onClose={() => setShowDecliningModal(false)} />
      )}

      {/* Forgotten Clients Modal */}
      {showForgottenModal && (
        <ForgottenClientsModal onClose={() => setShowForgottenModal(false)} />
      )}

      {/* Repeat Sales Modal */}
      {showRepeatSalesModal && (
        <RepeatSalesModal onClose={() => setShowRepeatSalesModal(false)} />
      )}

      {/* Unanswered Offers Modal */}
      {showUnansweredModal && (
        <UnansweredOffersModal onClose={() => setShowUnansweredModal(false)} />
      )}

      {/* Department Analysis Modal */}
      {showDeptAnalysisModal && (
        <DepartmentAnalysisModal onClose={() => setShowDeptAnalysisModal(false)} />
      )}

      {/* Client Analysis Modal (existing) */}
      {showClientModal && (
        <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-[#D97706]"></i>
                <span className="text-[12px] font-semibold text-[#2D2D2D]">Анализ клиентской базы</span>
              </div>
              <button onClick={() => setShowClientModal(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
                <i className="ri-close-line text-[#666666]"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
                <div className="p-2 bg-[#D4EDDA] rounded text-center">
                  <p className="text-[18px] font-bold text-[#2D6A4F]">{clientAnalysis.filter(c => c.status === "Активный").length}</p>
                  <p className="text-[10px] text-[#2D6A4F]">Активные</p>
                </div>
                <div className="p-2 bg-[#FEF3C7] rounded text-center">
                  <p className="text-[18px] font-bold text-[#D97706]">{clientAnalysis.filter(c => c.status === "Уснувший" || c.trend === "down").length}</p>
                  <p className="text-[10px] text-[#D97706]">Снижение/уснувшие</p>
                </div>
                <div className="p-2 bg-[#FEE2E2] rounded text-center">
                  <p className="text-[18px] font-bold text-[#DC2626]">{clientAnalysis.filter(c => c.debt > 0).length}</p>
                  <p className="text-[10px] text-[#DC2626]">С долгами</p>
                </div>
                <div className="p-2 bg-[#E8F4FD] rounded text-center">
                  <p className="text-[18px] font-bold text-[#0369A1]">{clientAnalysis.filter(c => c.trend === "down").length}</p>
                  <p className="text-[10px] text-[#0369A1]">Снижение</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-[10px] text-[#666666] px-2 py-1 bg-[#FDF2B5] rounded">
                  <span className="flex-1">Клиент</span>
                  <span className="w-[80px] text-right">Выручка</span>
                  <span className="w-[80px] text-right">Маржа</span>
                  <span className="w-[60px] text-right">Заказов</span>
                  <span className="w-[80px] text-right">Статус</span>
                  <span className="w-[60px] text-right">Тренд</span>
                </div>
                {clientAnalysis.map((c) => (
                  <div key={c.name} className="flex items-center text-[10px] px-2 py-1.5 border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/30 rounded transition-colors">
                    <span className="flex-1 text-[#2D2D2D] truncate">{c.name}</span>
                    <span className="w-[80px] text-right text-[#2D2D2D] font-medium">{formatMoney(c.totalRevenue)}</span>
                    <span className="w-[80px] text-right text-[#2D6A4F]">{formatMoney(c.totalMargin)}</span>
                    <span className="w-[60px] text-right text-[#666666]">{c.ordersCount}</span>
                    <span className="w-[80px] text-right">
                      <span className={`px-1 py-0.5 rounded text-[9px] ${
                        c.status === "Активный" ? "bg-[#D4EDDA] text-[#2D6A4F]" :
                        c.status === "Уснувший" ? "bg-[#FEE2E2] text-[#DC2626]" :
                        c.status === "Новый" ? "bg-[#E8F4FD] text-[#0369A1]" :
                        "bg-[#FEF3C7] text-[#D97706]"
                      }`}>{c.status}</span>
                    </span>
                    <span className={`w-[60px] text-right font-medium ${c.trend === "up" ? "text-[#2D6A4F]" : "text-[#DC2626]"}`}>
                      {c.trend === "up" ? "+" : ""}{c.trendPercent}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
              <button onClick={() => setShowClientModal(false)} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manager KPI Modal (existing - expanded) */}
      {showManagerKPIModal && (
        <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
              <div className="flex items-center gap-2">
                <i className="ri-bar-chart-line text-[#D97706]"></i>
                <span className="text-[12px] font-semibold text-[#2D2D2D]">KPI менеджеров — план/факт</span>
              </div>
              <button onClick={() => setShowManagerKPIModal(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
                <i className="ri-close-line text-[#666666]"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {managerKPIs.map((m) => (
                  <div key={m.name} className="p-3 border border-[#D5CA8A] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] font-semibold text-[#2D2D2D]">{m.name}</span>
                      <span className="text-[11px] text-[#666666]">Дисциплина: {(m.discipline * 100).toFixed(0)}%</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      <div className="text-center p-1 bg-[#FDF2B5] rounded">
                        <p className="text-[14px] font-bold text-[#2D2D2D]">{m.newClients}</p>
                        <p className="text-[9px] text-[#666666]">Новые клиенты</p>
                      </div>
                      <div className="text-center p-1 bg-[#FDF2B5] rounded">
                        <p className="text-[14px] font-bold text-[#2D2D2D]">{m.calls}</p>
                        <p className="text-[9px] text-[#666666]">Звонки</p>
                      </div>
                      <div className="text-center p-1 bg-[#FDF2B5] rounded">
                        <p className="text-[14px] font-bold text-[#2D2D2D]">{m.proposals}</p>
                        <p className="text-[9px] text-[#666666]">КП</p>
                      </div>
                      <div className="text-center p-1 bg-[#FDF2B5] rounded">
                        <p className="text-[14px] font-bold text-[#2D2D2D]">{(m.conversion * 100).toFixed(0)}%</p>
                        <p className="text-[9px] text-[#666666]">Конверсия</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-[#666666]">Выручка: <span className="text-[#2D2D2D] font-medium">{formatMoney(m.revenue)}</span></span>
                      <span className="text-[#666666]">Маржа: <span className="text-[#2D6A4F] font-medium">{formatMoney(m.margin)}</span></span>
                      <span className="text-[#666666]">Счетов: <span className="text-[#2D2D2D] font-medium">{m.invoices}</span></span>
                      <span className="text-[#666666]">Оплат: <span className="text-[#2D6A4F] font-medium">{m.payments}</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
              <button onClick={() => setShowManagerKPIModal(false)} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Funnel Modal (existing) */}
      {showFunnelModal && (
        <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
              <div className="flex items-center gap-2">
                <i className="ri-filter-3-line text-[#D97706]"></i>
                <span className="text-[12px] font-semibold text-[#2D2D2D]">Воронка продаж + зависшие сделки</span>
              </div>
              <button onClick={() => setShowFunnelModal(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
                <i className="ri-close-line text-[#666666]"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-[11px] font-semibold text-[#2D2D2D] mb-2">Воронка продаж</p>
              <div className="space-y-1 mb-4">
                {departmentStages.map((stage) => {
                  const maxCount = departmentStages[0].count;
                  const width = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
                  return (
                    <div key={stage.name} className="flex items-center gap-3">
                      <div className="w-[140px] text-[10px] text-[#2D2D2D] text-right truncate">{stage.name}</div>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="h-6 bg-[#FDF2B5] rounded flex items-center" style={{ width: `${width}%` }}>
                          <span className="text-[10px] font-medium text-[#2D2D2D] px-1">{stage.count}</span>
                        </div>
                        <span className="text-[10px] text-[#666666]">{(stage.conversion * 100).toFixed(0)}%</span>
                      </div>
                      {stage.lossReason && (
                        <span className="text-[9px] text-[#DC2626] bg-[#FEE2E2] px-1.5 py-0.5 rounded truncate max-w-[100px]">{stage.lossReason}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="text-[11px] font-semibold text-[#2D2D2D] mb-2">Зависшие сделки по этапам ({stuckDeals.length})</p>
              <div className="space-y-1">
                {stuckDeals.sort((a, b) => b.daysWithoutAction - a.daysWithoutAction).map((deal) => (
                  <div key={deal.id} className="flex items-center text-[10px] px-2 py-1 border-b border-[#D5CA8A]/30">
                    <span className="flex-1 text-[#2D2D2D] truncate">{deal.client.replace("ООО ", "").replace("АО ", "")}</span>
                    <span className="w-[80px] text-right text-[#666666]">{formatMoney(deal.amount)}</span>
                    <span className="w-[70px] text-right text-[#666666]">{deal.manager}</span>
                    <span className={`w-[60px] text-right font-medium ${deal.daysWithoutAction > 30 ? "text-[#DC2626]" : "text-[#D97706]"}`}>{deal.daysWithoutAction} дн.</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-[#FEF3C7] rounded-lg">
                <p className="text-[11px] font-semibold text-[#2D2D2D] mb-1">Проблемные зоны</p>
                <p className="text-[10px] text-[#666666]">
                  Наибольшая потеря на этапе "Презентация / КП" — конверсия 55%. Возможные причины: цена выше конкурентов, недостаточно убедительные презентации.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
              <button onClick={() => setShowFunnelModal(false)} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal (existing) */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
              <div className="flex items-center gap-2">
                <i className="ri-customer-service-line text-[#D97706]"></i>
                <span className="text-[12px] font-semibold text-[#2D2D2D]">Помощь менеджерам</span>
              </div>
              <button onClick={() => setShowHelpModal(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
                <i className="ri-close-line text-[#666666]"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-3">
                {managerHelpItems.map((item, i) => (
                  <div key={i} className="p-3 border border-[#D5CA8A] rounded-lg hover:bg-[#FDF2B5]/30 transition-colors cursor-pointer">
                    <div className="flex items-start gap-2">
                      <i className="ri-lightbulb-line text-[#D97706] text-[12px] mt-0.5"></i>
                      <div>
                        <p className="text-[11px] font-medium text-[#2D2D2D]">{item}</p>
                        <p className="text-[10px] text-[#666666] mt-0.5">
                          AI подготовит рекомендации на основе истории взаимодействий и текущих данных.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
              <button onClick={() => setShowHelpModal(false)} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tasks List Modal (existing) */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
              <div className="flex items-center gap-2">
                <i className="ri-task-line text-[#D97706]"></i>
                <span className="text-[12px] font-semibold text-[#2D2D2D]">Все задачи ({tasks.length})</span>
              </div>
              <button onClick={() => setShowTaskModal(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
                <i className="ri-close-line text-[#666666]"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] text-[#666666] px-2 py-1 bg-[#FDF2B5] rounded">
                  <span>Задача</span>
                  <span className="flex items-center gap-4">
                    <span>Исполнитель</span>
                    <span>Дедлайн</span>
                    <span>Приоритет</span>
                  </span>
                </div>
                {tasks.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-[11px] px-2 py-1.5 border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/30 rounded transition-colors">
                    <span className="text-[#2D2D2D] truncate max-w-[200px]">{t.title}</span>
                    <span className="flex items-center gap-4">
                      <span className="text-[#666666]">{t.assignee}</span>
                      <span className="text-[#666666]">{t.deadline}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                        t.priority === "Высокий" ? "bg-[#FEE2E2] text-[#DC2626]" : t.priority === "Средний" ? "bg-[#FEF3C7] text-[#D97706]" : "bg-[#D4EDDA] text-[#2D6A4F]"
                      }`}>{t.priority}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
              <button onClick={() => setShowTaskModal(false)} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}